import { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import type { GracePhase } from "@/contexts/MaintenanceContext";

interface CountdownTimerProps {
  targetTime: Date;
  size?: "sm" | "md" | "lg" | "xl";
  format?: "mm:ss" | "human";
  onPhaseChange?: (phase: GracePhase) => void;
  onComplete?: () => void;
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<CountdownTimerProps["size"]>, string> = {
  sm: "text-sm tabular-nums",
  md: "text-lg font-semibold tabular-nums",
  lg: "text-2xl font-bold tabular-nums",
  xl: "text-5xl font-bold tabular-nums",
};

function getPhaseFromSeconds(seconds: number): GracePhase {
  if (seconds <= 0) return null;
  if (seconds <= 10) return "logout";
  if (seconds <= 30) return "readonly";
  if (seconds <= 60) return "save";
  return "warn";
}

function formatTime(totalSeconds: number, format: "mm:ss" | "human"): string {
  const clamped = Math.max(0, totalSeconds);
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;

  if (format === "mm:ss") {
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  if (mins > 0) {
    return `${mins} min ${secs}s`;
  }
  return `${secs}s`;
}

function formatAriaLabel(totalSeconds: number): string {
  const clamped = Math.max(0, totalSeconds);
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;

  if (mins > 0 && secs > 0) {
    return `Tempo restante: ${mins} minutos e ${secs} segundos`;
  }
  if (mins > 0) {
    return `Tempo restante: ${mins} minutos`;
  }
  return `Tempo restante: ${secs} segundos`;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetTime,
  size = "md",
  format = "mm:ss",
  onPhaseChange,
  onComplete,
  className,
}) => {
  const calcRemaining = useCallback(
    () => Math.max(0, Math.ceil((targetTime.getTime() - Date.now()) / 1000)),
    [targetTime]
  );

  const [secondsRemaining, setSecondsRemaining] = useState(calcRemaining);
  const [ariaLabel, setAriaLabel] = useState(() => formatAriaLabel(calcRemaining()));

  const rafRef = useRef<number>(0);
  const lastPhaseRef = useRef<GracePhase>(getPhaseFromSeconds(calcRemaining()));
  const lastAriaUpdateRef = useRef<number>(Date.now());
  const completedRef = useRef(false);
  const onPhaseChangeRef = useRef(onPhaseChange);
  const onCompleteRef = useRef(onComplete);

  // Keep callback refs current
  useEffect(() => {
    onPhaseChangeRef.current = onPhaseChange;
  }, [onPhaseChange]);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    completedRef.current = false;
    lastPhaseRef.current = getPhaseFromSeconds(calcRemaining());
    lastAriaUpdateRef.current = Date.now();

    const tick = () => {
      const remaining = calcRemaining();
      setSecondsRemaining(remaining);

      // Phase change detection
      const newPhase = getPhaseFromSeconds(remaining);
      if (newPhase !== lastPhaseRef.current) {
        lastPhaseRef.current = newPhase;
        onPhaseChangeRef.current?.(newPhase);
      }

      // Update aria-label every 15s
      const now = Date.now();
      if (now - lastAriaUpdateRef.current >= 15_000) {
        setAriaLabel(formatAriaLabel(remaining));
        lastAriaUpdateRef.current = now;
      }

      // Completion
      if (remaining <= 0 && !completedRef.current) {
        completedRef.current = true;
        setAriaLabel(formatAriaLabel(0));
        onCompleteRef.current?.();
        return;
      }

      if (remaining > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [calcRemaining]);

  return (
    <span className={cn(SIZE_CLASSES[size], className)} aria-label={ariaLabel} role="timer">
      {formatTime(secondsRemaining, format)}
    </span>
  );
};
